import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO 
        title="Privacy Policy | Paint It Better"
        description="How Paint It Better, painters and decorators in Wye, Kent, collect, use and protect the personal information you give us when you ask for a quote."
        canonical="/privacy"
      />
      
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-cyan-100">
              How we handle your personal information
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 mb-6">
                Last updated: December 2025
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Who We Are</h2>
              <p className="text-slate-600 mb-6">
                Paint It Better Ltd is a painting and decorating company based in Wye, Kent. 
                We are committed to protecting your privacy and handling your personal information responsibly.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">What Information We Collect</h2>
              <p className="text-slate-600 mb-4">
                When you contact us through our website, we collect:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Your name</li>
                <li>Your email address</li>
                <li>Your phone number (if provided)</li>
                <li>The content of your message</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">How We Use Your Information</h2>
              <p className="text-slate-600 mb-4">
                We use your information solely to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Respond to your enquiry</li>
                <li>Provide you with a quote for our services</li>
                <li>Communicate with you about your project</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Data Sharing</h2>
              <p className="text-slate-600 mb-6">
                <strong>We never share your personal information with third parties.</strong> Your details 
                are kept strictly confidential and are only used for the purpose of responding to your 
                enquiry and providing our services.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Data Retention</h2>
              <p className="text-slate-600 mb-6">
                We keep your contact information only as long as necessary to respond to your enquiry 
                and provide our services. If you become a customer, we retain your information for the 
                duration of our business relationship and as required by law for accounting purposes.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Your Rights</h2>
              <p className="text-slate-600 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
                <li>Request access to your personal data</li>
                <li>Request correction of your personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to processing of your personal data</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Contact Us</h2>
              <p className="text-slate-600 mb-6">
                If you have any questions about this Privacy Policy or wish to exercise your rights, 
                please contact us at{' '}
                <a href="mailto:hello@paint-it-better.com" className="text-cyan-600 hover:text-cyan-700">
                  hello@paint-it-better.com
                </a>
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <Link to="/contact" className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-medium">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Contact
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
